import { DownOutlined } from '@ant-design/icons';
import { Alert, Form, Switch, Tooltip } from 'antd';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CommonSelect from '../../../../../../../../components/CommonSelect';
import CommonTable from '../../../../../../../../components/CommonTable';

const TruncatedUserCell = ({ users, onClick }) => {
  const firstName = users?.[0]?.name;
  const remainingCount = (users?.length || 0) - 1;

  return (
    <div
      className="editable-cell-value-wrap d-flex justify-between align-center readonly-select"
      onClick={onClick}
    >
      <div className="d-flex align-center ellipsis-text">
        <span className="gx-text-nowrap">
          {firstName || 'All Approvers Removed'}
        </span>
        {remainingCount > 0 && (
          <Tooltip
            title={users?.map((u) => u?.name)?.join(', ')}
            placement="left"
          >
            <span className="remaining-count ml-5 gx-text-nowrap">{`+${remainingCount}`}</span>
          </Tooltip>
        )}
      </div>
      <DownOutlined />
    </div>
  );
};

const EditableContext = createContext(null);

const EditableRow = ({ ...props }) => {
  const [editableForm] = Form.useForm();
  return (
    <Form form={editableForm} component={false}>
      <EditableContext.Provider value={editableForm}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  editable,
  children,
  dataIndex,
  record,
  title,
  levelEnabled,
  removalsMap,
  activeCell,
  isOpen,
  sortedLevelNumbers,
  enabledLevels,
  handleSave,
  handleCellClick,
  ...restProps
}) => {
  const editableForm = useContext(EditableContext);
  const inputRef = useRef(null);

  const stageId = record?.key;
  const levelNumber = dataIndex?.replace('level_', '');
  const cellData = record?.[dataIndex] || {};
  const internalUsers = cellData?.internalUsers || [];
  const externalUsers = cellData?.externalUsers || [];
  const allUsers = [...internalUsers, ...externalUsers];
  const cellRemovals = removalsMap?.[stageId]?.[levelNumber];
  const isCellActive =
    activeCell?.rowKey === record?.key && activeCell?.columnKey === dataIndex;

  const allUserIds = allUsers?.map((user) => user?.id) || [];
  const deselectedIds = cellRemovals
    ? new Set([
        ...(cellRemovals?.internalIds || []),
        ...(cellRemovals?.externalIds || []),
      ])
    : null;
  const initialSelectedIds = deselectedIds
    ? allUserIds?.filter((id) => !deselectedIds?.has(id))
    : allUserIds;

  // Check if this cell can allow full removal (highest level logic per-cell)
  const currentLevelNum = Number(levelNumber);
  const higherLevels = sortedLevelNumbers?.filter(
    (lvl) => lvl > currentLevelNum && enabledLevels?.[lvl] !== false,
  );
  const isHighestLevel = higherLevels?.every((lvl) => {
    const higherCellData = record?.[`level_${lvl}`] || {};
    const higherTotalUsers =
      (higherCellData?.internalUsers?.length || 0) +
      (higherCellData?.externalUsers?.length || 0);
    if (higherTotalUsers === 0) return true;
    const higherRemovals = removalsMap?.[stageId]?.[lvl];
    if (!higherRemovals) return false;
    const higherRemovedCount =
      (higherRemovals?.internalIds?.length || 0) +
      (higherRemovals?.externalIds?.length || 0);
    return higherRemovedCount >= higherTotalUsers;
  });

  const isFirstOpen = useRef(true);

  useEffect(() => {
    if (isOpen && isCellActive) {
      inputRef?.current?.focus();
      if (isFirstOpen.current) {
        editableForm?.setFieldsValue({
          [dataIndex]: initialSelectedIds,
        });
        isFirstOpen.current = false;
      }
    }
    if (!isCellActive) {
      isFirstOpen.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isCellActive]);

  const save = async () => {
    try {
      const values = await editableForm?.validateFields();
      handleSave?.({
        stageId,
        levelNumber,
        dataIndex,
        values,
        title,
        internalUsers,
        allUserIds,
      });
    } catch (errInfo) {
      const currentValues = editableForm?.getFieldsValue();
      handleSave?.({
        stageId,
        levelNumber,
        dataIndex,
        values: currentValues,
        title,
        internalUsers,
        allUserIds,
        validationFailed: true,
      });
    }
  };

  const selectOptions = useMemo(
    () => [
      ...(internalUsers?.length > 0
        ? [
            {
              label: 'Internal Approvers',
              options: internalUsers?.map((user) => ({
                label: user?.name,
                value: user?.id,
              })),
            },
          ]
        : []),
      ...(externalUsers?.length > 0
        ? [
            {
              label: 'External Approvers',
              options: externalUsers?.map((user) => ({
                label: user?.name,
                value: user?.id,
              })),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stageId, levelNumber],
  );

  const noDataPlaceholder = (
    <Tooltip title="You can only remove approvers or turn off the level. To add approvers, go to Assign Approvers.">
      <div className="editable-cell-value-wrap disabled-level-cell">
        <span className="">-</span>
      </div>
    </Tooltip>
  );

  let childNode = children;
  const isLevelOff = editable && levelEnabled === false;

  if (isLevelOff) {
    const displayUsers = deselectedIds
      ? allUsers?.filter((user) => !deselectedIds?.has(user?.id))
      : allUsers;
    childNode =
      allUsers?.length > 0 ? (
        <TruncatedUserCell users={displayUsers} onClick={() => {}} />
      ) : (
        noDataPlaceholder
      );
  } else if (editable && allUsers?.length === 0) {
    childNode = noDataPlaceholder;
  } else if (editable && allUsers?.length > 0) {
    childNode = isCellActive ? (
      <Form.Item
        name={dataIndex}
        className="mb-0"
        initialValue={initialSelectedIds}
        rules={[
          {
            validator: (_, value) => {
              if (value?.length > 0) return Promise.resolve();
              if (isHighestLevel) return Promise.resolve();
              return Promise.reject(
                new Error('At least one approver must remain'),
              );
            },
          },
        ]}
      >
        <CommonSelect
          inputRef={inputRef}
          mode="multiple"
          showSearch
          allowClear
          autoFocus
          optionFilterProp="label"
          placeholder="All Approvers Removed"
          open={isOpen && isCellActive}
          onFocus={() => handleCellClick?.(record?.key, dataIndex)}
          onBlur={save}
          maxTagCount={1}
          maxTagPlaceholder={(omittedValues) => (
            <Tooltip
              title={omittedValues?.map((value) => value?.label)?.join(', ')}
              placement="left"
            >
              <span>{`+${omittedValues?.length}`}</span>
            </Tooltip>
          )}
          className="width-percent-100"
          virtual={false}
          getPopupContainer={(trigger) =>
            trigger?.closest('.remove-approvers-step') || trigger?.parentElement
          }
          options={selectOptions}
        />
      </Form.Item>
    ) : (
      <>
        <TruncatedUserCell
          users={allUsers?.filter((user) => !deselectedIds?.has(user?.id))}
          onClick={() => {
            handleCellClick?.(record?.key, dataIndex);
          }}
        />
        {deselectedIds && initialSelectedIds?.length === 0 && (
          <div className={isHighestLevel ? 'warning-text' : 'danger-text'}>
            {isHighestLevel
              ? 'This stage will be turned off'
              : 'At least one approver must remain'}
          </div>
        )}
      </>
    );
  }

  const tdClassName = isLevelOff ? 'level-off-cell' : '';
  const existingClassName = restProps?.className || '';
  const combinedClassName =
    `${existingClassName} ${tdClassName}`?.trim() || undefined;

  return (
    <td {...restProps} className={combinedClassName}>
      {childNode}
    </td>
  );
};

const tableComponents = {
  body: {
    row: EditableRow,
    cell: EditableCell,
  },
};

const RemoveApproversStep = ({
  onRemovalsChange,
  onEnabledLevelsChange,
  onRfiRemovalsChange,
  onRfiEnabledLevelsChange,
  onValidationErrorChange,
  prefetchedStageData,
}) => {
  const stageData = prefetchedStageData || [];
  const [activeCell, setActiveCell] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [removalsMap, setRemovalsMap] = useState({});
  const [enabledLevels, setEnabledLevels] = useState({});
  const [hasValidationError, setHasValidationError] = useState(false);

  // RFI removal state
  const [rfiActiveCell, setRfiActiveCell] = useState(null);
  const [rfiIsOpen, setRfiIsOpen] = useState(false);
  const [rfiRemovalsMap, setRfiRemovalsMap] = useState({});
  const [rfiEnabledLevels, setRfiEnabledLevels] = useState({});

  useEffect(() => {
    onRemovalsChange?.(removalsMap);
  }, [removalsMap, onRemovalsChange]);

  useEffect(() => {
    onValidationErrorChange?.(hasValidationError);
  }, [hasValidationError, onValidationErrorChange]);

  useEffect(() => {
    onRfiRemovalsChange?.(rfiRemovalsMap);
  }, [rfiRemovalsMap, onRfiRemovalsChange]);

  const handleLevelToggle = useCallback((levelNumber, checked) => {
    setEnabledLevels((prev) => {
      const next = { ...prev, [levelNumber]: checked };
      return next;
    });

    if (!checked) {
      setHasValidationError(false);
    }
  }, []);

  const handleRfiLevelToggle = useCallback((levelNumber, checked) => {
    setRfiEnabledLevels((prev) => {
      const next = { ...prev, [levelNumber]: checked };
      return next;
    });

    if (!checked) {
      setHasValidationError(false);
    }
  }, []);

  const {
    tableData,
    levelColumns,
    sortedLevelNumbers,
    levelNames,
    hasStageData,
  } = useMemo(() => {
    const stages = stageData || [];
    const levelNameMap = {};

    stages?.forEach((stage) => {
      stage?.levels?.forEach((level) => {
        if (!levelNameMap[level?.levelNumber]) {
          levelNameMap[level?.levelNumber] = `Level ${level?.levelNumber}`;
        }
      });
    });

    const sortedLevels = Object.keys(levelNameMap)
      ?.map(Number)
      ?.sort((a, b) => a - b);

    const rows = stages?.map((stage) => {
      const row = {
        key: stage?.id,
        stageName: stage?.name,
      };

      stage?.levels?.forEach((level) => {
        row[`level_${level?.levelNumber}`] = {
          internalUsers: level?.internalUsers || [],
          externalUsers: level?.externalUsers || [],
        };
      });

      return row;
    });

    const levelColumnWidth =
      sortedLevels?.length > 0
        ? `${Math?.floor(100 / (sortedLevels?.length + 1))}%`
        : undefined;

    const dynamicColumns = sortedLevels?.map((levelNumber) => ({
      title: levelNameMap[levelNumber],
      dataIndex: `level_${levelNumber}`,
      key: `level_${levelNumber}`,
      editable: true,
      levelNumber,
      width: levelColumnWidth,
    }));

    const hasStageApprovers = rows?.some((row) =>
      sortedLevels?.some((lvl) => {
        const cell = row[`level_${lvl}`];
        return (
          cell?.internalUsers?.length > 0 || cell?.externalUsers?.length > 0
        );
      }),
    );

    return {
      tableData: rows,
      levelColumns: dynamicColumns,
      sortedLevelNumbers: sortedLevels,
      levelNames: levelNameMap,
      hasStageData: hasStageApprovers,
    };
  }, [stageData]);

  useEffect(() => {
    if (sortedLevelNumbers?.length > 0) {
      const initial = {};
      sortedLevelNumbers?.forEach((lvl) => {
        initial[lvl] = true;
      });
      setEnabledLevels(initial);
    }
  }, [sortedLevelNumbers]);

  useEffect(() => {
    onEnabledLevelsChange?.({ enabledLevels, levelNames });
  }, [enabledLevels, levelNames, onEnabledLevelsChange]);

  const handleSave = useCallback(
    ({
      stageId,
      levelNumber,
      dataIndex,
      values,
      title: cellTitle,
      internalUsers,
      allUserIds,
      close,
      validationFailed,
    }) => {
      if (validationFailed) {
        const selectedIds = new Set(values?.[dataIndex] || []);
        const internalUserIds = new Set(internalUsers?.map((u) => u?.id));
        const removedUserIds = allUserIds?.filter(
          (id) => !selectedIds?.has(id),
        );
        setRemovalsMap((prev) => ({
          ...prev,
          [stageId]: {
            ...prev?.[stageId],
            [levelNumber]: {
              levelName: cellTitle,
              internalIds: removedUserIds?.filter((id) =>
                internalUserIds?.has(id),
              ),
              externalIds: removedUserIds?.filter(
                (id) => !internalUserIds?.has(id),
              ),
            },
          },
        }));
        setActiveCell(null);
        setIsOpen(false);
        setHasValidationError(true);
        return;
      }
      if (close) {
        setActiveCell(null);
        setIsOpen(false);
        return;
      }
      setHasValidationError(false);
      const selectedIds = new Set(values?.[dataIndex] || []);
      const internalUserIds = new Set(internalUsers?.map((u) => u?.id));
      const removedUserIds = allUserIds?.filter((id) => !selectedIds?.has(id));
      setRemovalsMap((prev) => ({
        ...prev,
        [stageId]: {
          ...prev?.[stageId],
          [levelNumber]: {
            levelName: cellTitle,
            internalIds: removedUserIds?.filter((id) =>
              internalUserIds?.has(id),
            ),
            externalIds: removedUserIds?.filter(
              (id) => !internalUserIds?.has(id),
            ),
          },
        },
      }));
      setActiveCell(null);
      setIsOpen(false);
    },
    [],
  );

  const handleCellClick = useCallback((rowKey, columnKey) => {
    setIsOpen(true);
    setHasValidationError(false);
    setActiveCell({ rowKey, columnKey });
  }, []);

  const baseColumns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Stages',
      dataIndex: 'stageName',
      key: 'stageName',
      width: 200,
    },
    ...levelColumns,
  ];

  const columns = baseColumns?.map((col) => {
    if (!col?.editable) return col;

    const { levelNumber } = col;
    const isEnabled = enabledLevels[levelNumber] !== false;
    const hasHigherEnabled = sortedLevelNumbers?.some(
      (lvl) => lvl > levelNumber && enabledLevels[lvl] !== false,
    );
    const hasLowerDisabled = sortedLevelNumbers?.some(
      (lvl) => lvl < levelNumber && enabledLevels[lvl] === false,
    );
    const isSwitchDisabled =
      (isEnabled && hasHigherEnabled) || (!isEnabled && hasLowerDisabled);
    const getSwitchTooltip = () => {
      if (isEnabled && hasHigherEnabled) return 'Disable higher levels first';
      if (!isEnabled && hasLowerDisabled) return 'Enable lower levels first';
      return '';
    };

    return {
      ...col,
      title: (
        <div className="d-flex align-center gap-8">
          <span>{col?.title}</span>
          <Tooltip title={getSwitchTooltip()}>
            <Switch
              size="small"
              checked={isEnabled}
              disabled={isSwitchDisabled}
              onChange={(checked) => handleLevelToggle(levelNumber, checked)}
            />
          </Tooltip>
        </div>
      ),
      onCell: (record) => ({
        record,
        editable: col?.editable,
        dataIndex: col?.dataIndex,
        title: col?.title,
        levelEnabled: isEnabled,
        sortedLevelNumbers,
        enabledLevels,
        removalsMap,
        activeCell,
        isOpen,
        handleSave,
        handleCellClick,
      }),
    };
  });

  // RFI table data
  const { rfiTableData, rfiLevelColumns, sortedRfiLevelNumbers, hasRfiData } =
    useMemo(() => {
      const stages = stageData || [];
      const rfiStages = stages?.filter(
        (stage) => stage?.isRfiActive && stage?.rfiLevels?.length > 0,
      );

      if (!rfiStages?.length) {
        return {
          rfiTableData: [],
          rfiLevelColumns: [],
          sortedRfiLevelNumbers: [],
          hasRfiData: false,
        };
      }

      const rfiLevelNameMap = {};
      rfiStages?.forEach((stage) => {
        stage?.rfiLevels?.forEach((level) => {
          if (!rfiLevelNameMap[level?.levelNumber]) {
            rfiLevelNameMap[level?.levelNumber] = 'RFI Approver';
          }
        });
      });

      const sortedRfiLevels = Object.keys(rfiLevelNameMap)
        ?.map(Number)
        ?.sort((a, b) => a - b);

      const rows = rfiStages?.map((stage) => {
        const row = {
          key: stage?.rfiStageId || stage?.id,
          stageName: stage?.name,
        };

        stage?.rfiLevels?.forEach((level) => {
          row[`level_${level?.levelNumber}`] = {
            internalUsers: level?.internalUsers || [],
            externalUsers: level?.externalUsers || [],
          };
        });

        return row;
      });

      const dynamicColumns = sortedRfiLevels?.map((levelNumber) => ({
        title: rfiLevelNameMap[levelNumber],
        dataIndex: `level_${levelNumber}`,
        key: `rfi_level_${levelNumber}`,
        editable: true,
        levelNumber,
      }));

      return {
        rfiTableData: rows,
        rfiLevelColumns: dynamicColumns,
        sortedRfiLevelNumbers: sortedRfiLevels,
        hasRfiData: true,
      };
    }, [stageData]);

  useEffect(() => {
    if (sortedRfiLevelNumbers?.length > 0) {
      const initial = {};
      sortedRfiLevelNumbers?.forEach((lvl) => {
        initial[lvl] = true;
      });
      setRfiEnabledLevels(initial);
    }
  }, [sortedRfiLevelNumbers]);

  useEffect(() => {
    onRfiEnabledLevelsChange?.(rfiEnabledLevels);
  }, [rfiEnabledLevels, onRfiEnabledLevelsChange]);

  const handleRfiSave = useCallback(
    ({
      stageId,
      levelNumber,
      dataIndex,
      values,
      title: cellTitle,
      internalUsers,
      allUserIds,
      close,
      validationFailed,
    }) => {
      if (validationFailed) {
        const selectedIds = new Set(values?.[dataIndex] || []);
        const internalUserIds = new Set(internalUsers?.map((u) => u?.id));
        const removedUserIds = allUserIds?.filter(
          (id) => !selectedIds?.has(id),
        );
        setRfiRemovalsMap((prev) => ({
          ...prev,
          [stageId]: {
            ...prev?.[stageId],
            [levelNumber]: {
              levelName: cellTitle,
              internalIds: removedUserIds?.filter((id) =>
                internalUserIds?.has(id),
              ),
              externalIds: removedUserIds?.filter(
                (id) => !internalUserIds?.has(id),
              ),
            },
          },
        }));
        setRfiActiveCell(null);
        setRfiIsOpen(false);
        setHasValidationError(true);
        return;
      }
      if (close) {
        setRfiActiveCell(null);
        setRfiIsOpen(false);
        return;
      }
      setHasValidationError(false);
      const selectedIds = new Set(values?.[dataIndex] || []);
      const internalUserIds = new Set(internalUsers?.map((u) => u?.id));
      const removedUserIds = allUserIds?.filter((id) => !selectedIds?.has(id));
      setRfiRemovalsMap((prev) => ({
        ...prev,
        [stageId]: {
          ...prev?.[stageId],
          [levelNumber]: {
            levelName: cellTitle,
            internalIds: removedUserIds?.filter((id) =>
              internalUserIds?.has(id),
            ),
            externalIds: removedUserIds?.filter(
              (id) => !internalUserIds?.has(id),
            ),
          },
        },
      }));
      setRfiActiveCell(null);
      setRfiIsOpen(false);
    },
    [],
  );

  const handleRfiCellClick = useCallback((rowKey, columnKey) => {
    setRfiIsOpen(true);
    setHasValidationError(false);
    setRfiActiveCell({ rowKey, columnKey });
  }, []);

  const rfiBaseColumns = [
    {
      title: '#',
      key: 'rfi_index',
      width: 50,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Stages',
      dataIndex: 'stageName',
      key: 'rfi_stageName',
      width: 200,
    },
    ...rfiLevelColumns,
  ];

  const rfiColumns = rfiBaseColumns?.map((col) => {
    if (!col?.editable) return col;

    const { levelNumber } = col;
    const isEnabled = rfiEnabledLevels[levelNumber] !== false;
    const hasHigherEnabled = sortedRfiLevelNumbers?.some(
      (lvl) => lvl > levelNumber && rfiEnabledLevels[lvl] !== false,
    );
    const isSwitchDisabled = isEnabled && hasHigherEnabled;

    return {
      ...col,
      title: (
        <div className="d-flex align-center gap-8">
          <span>{col?.title}</span>
          <Tooltip
            title={isSwitchDisabled ? 'Disable higher levels first' : ''}
          >
            <Switch
              size="small"
              checked={isEnabled}
              disabled={isSwitchDisabled}
              onChange={(checked) => handleRfiLevelToggle(levelNumber, checked)}
            />
          </Tooltip>
        </div>
      ),
      onCell: (record) => ({
        record,
        editable: col?.editable,
        dataIndex: col?.dataIndex,
        title: col?.title,
        levelEnabled: isEnabled,
        sortedLevelNumbers: sortedRfiLevelNumbers,
        enabledLevels: rfiEnabledLevels,
        removalsMap: rfiRemovalsMap,
        activeCell: rfiActiveCell,
        isOpen: rfiIsOpen,
        handleSave: handleRfiSave,
        handleCellClick: handleRfiCellClick,
      }),
    };
  });

  return (
    <div className="remove-approvers-step">
      <Alert
        showIcon
        title="Note: Only the current changes will be updated. All other assignments will remain unchanged."
        className="mb-10"
      />
      {hasRfiData && (
        <>
          <div className="fw-semi-bold font-size-16 mb-10">RFI Approvers</div>
          <CommonTable
            id="remove-rfi-approvers-table"
            columns={rfiColumns}
            components={tableComponents}
            data={rfiTableData}
            rowKey="key"
          />
        </>
      )}
      {hasStageData && (
        <>
          <div className="fw-semi-bold font-size-16 mb-10">Stage Approvers</div>
          <CommonTable
            id="remove-approvers-table"
            columns={columns}
            components={tableComponents}
            data={tableData}
            rowKey="key"
          />
        </>
      )}
    </div>
  );
};

export default RemoveApproversStep;
